import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import Creator from "./pages/Creator";
import GameDetail from "./pages/GameDetail";
import SubmissionEntry from "./pages/SubmissionEntry";
import EmbedGallery from "./pages/EmbedGallery";
import Games from "./pages/Games";
import Partner from "./pages/Partner";
import PartnerUnsubscribe from "./pages/PartnerUnsubscribe";
import ProfileSetup from "./pages/ProfileSetup";
import SignupSuccess from "./pages/SignupSuccess";
import VerifyEmail from "./pages/VerifyEmail";
import Players from "./pages/Players";
import Creators from "./pages/Creators";
import Promoters from "./pages/Promoters";
import Leaderboard from "./pages/Leaderboard";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AcceptableUse from "./pages/AcceptableUse";
import FAQ from "./pages/FAQ";
import Pricing from "./pages/Pricing";
import Support from "./pages/Support";
import GamePointDashboard from "./components/GamePointDashboard";
import { lazy, Suspense } from "react";
import { useReferralCapture } from "./hooks/useReferralCapture";
import AdminGuard from "./components/AdminGuard";
import Login from "./pages/Login";

// Lazy-load console pages for code splitting
const DashboardHome = lazy(() => import("./pages/console/DashboardHome"));
const Tournaments = lazy(() => import("./pages/console/Tournaments"));
const QuickCreate = lazy(() => import("./pages/console/QuickCreate"));
const WalletPage = lazy(() => import("./pages/console/WalletPage"));
const ProfilePage = lazy(() => import("./pages/console/ProfilePage"));
const ReferralPage = lazy(() => import("./pages/console/ReferralPage"));
const WithdrawPage = lazy(() => import("./pages/console/WithdrawPage"));
const SettlesPage = lazy(() => import("./pages/console/SettlesPage"));
const CouponsPage = lazy(() => import("./pages/console/CouponsPage"));
const RewardsPage = lazy(() => import("./pages/console/RewardsPage"));
const GameTypesPage = lazy(() => import("./pages/console/GameTypesPage"));
const SurveyPage = lazy(() => import("./pages/console/SurveyPage"));
const SettingsPage = lazy(() => import("./pages/console/SettingsPage"));
const NotificationsPage = lazy(
  () => import("./pages/console/NotificationsPage")
);
const AdminOverview = lazy(() => import("./pages/console/AdminOverview"));
const OutreachPage = lazy(() => import("./pages/console/OutreachPage"));
const ChallengesPage = lazy(() => import("./pages/console/ChallengesPage"));
const AgentManagement = lazy(() => import("./pages/console/AgentManagement"));
const AgentActivity = lazy(() => import("./pages/console/AgentActivity"));
const AgentApprovals = lazy(() => import("./pages/console/AgentApprovals"));
const DisputesPage = lazy(() => import("./pages/console/DisputesPage"));
const AdminUsersPage = lazy(() => import("./pages/console/AdminUsersPage"));
const AiModelsPage = lazy(() => import("./pages/console/AiModelsPage"));
const AiForgePage = lazy(() => import("./pages/console/AiForgePage"));
const AiForgeLogsPage = lazy(() => import("./pages/console/AiForgeLogsPage"));
const AiForgeRunsPage = lazy(() => import("./pages/console/AiForgeRunsPage"));
const AiForgeRunDetailPage = lazy(
  () => import("./pages/console/AiForgeRunDetailPage")
);

function ConsoleFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-neon/30 border-t-teal rounded-full animate-spin" />
    </div>
  );
}

function ConsoleRouter() {
  return (
    <GamePointDashboard>
      <Suspense fallback={<ConsoleFallback />}>
        <Switch>
          <Route path="/console" component={DashboardHome} />
          <Route path="/console/tournaments" component={Tournaments} />
          <Route path="/console/quick-create" component={QuickCreate} />
          <Route path="/console/wallet" component={WalletPage} />
          <Route path="/console/profile" component={ProfilePage} />
          <Route path="/console/referral" component={ReferralPage} />
          <Route path="/console/withdraw">
            {() => (
              <AdminGuard>
                <WithdrawPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/settles">
            {() => (
              <AdminGuard>
                <SettlesPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/coupons">
            {() => (
              <AdminGuard>
                <CouponsPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/rewards">
            {() => (
              <AdminGuard>
                <RewardsPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/gametypes">
            {() => (
              <AdminGuard>
                <GameTypesPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/survey">
            {() => (
              <AdminGuard>
                <SurveyPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/admin-overview">
            {() => (
              <AdminGuard>
                <AdminOverview />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/outreach">
            {() => (
              <AdminGuard>
                <OutreachPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/agents">
            {() => (
              <AdminGuard>
                <AgentManagement />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/agent-activity">
            {() => (
              <AdminGuard>
                <AgentActivity />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/agent-approvals">
            {() => (
              <AdminGuard>
                <AgentApprovals />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/disputes">
            {() => (
              <AdminGuard>
                <DisputesPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/admin-users">
            {() => (
              <AdminGuard>
                <AdminUsersPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/ai-models">
            {() => (
              <AdminGuard>
                <AiModelsPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/ai-forge/runs/:runId">
            {() => (
              <AdminGuard>
                <AiForgeRunDetailPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/ai-forge/logs">
            {() => (
              <AdminGuard>
                <AiForgeLogsPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/ai-forge/runs">
            {() => (
              <AdminGuard>
                <AiForgeRunsPage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/ai-forge">
            {() => (
              <AdminGuard>
                <AiForgePage />
              </AdminGuard>
            )}
          </Route>
          <Route path="/console/settings" component={SettingsPage} />
          <Route path="/console/challenges" component={ChallengesPage} />
          <Route path="/console/notifications" component={NotificationsPage} />
          <Route component={DashboardHome} />
        </Switch>
      </Suspense>
    </GamePointDashboard>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/create" component={Creator} />
      <Route path="/games" component={Games} />
      <Route path="/game/:slug" component={GameDetail} />
      <Route path="/game/:slug/entry/:shareToken" component={SubmissionEntry} />
      <Route path="/embed/:slug" component={EmbedGallery} />
      <Route path="/players" component={Players} />
      <Route path="/creators" component={Creators} />
      <Route path="/promoters" component={Promoters} />
      <Route path="/leaderboard" component={Leaderboard} />
      <Route
        path="/partner/:token/unsubscribe"
        component={PartnerUnsubscribe}
      />
      <Route path="/partner/:token" component={Partner} />
      <Route path="/profile-setup" component={ProfileSetup} />
      <Route path="/signup-success" component={SignupSuccess} />
      <Route path="/verify-email" component={VerifyEmail} />
      <Route path="/terms" component={Terms} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/acceptable-use" component={AcceptableUse} />
      <Route path="/faq" component={FAQ} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/support" component={Support} />
      <Route path="/login" component={Login} />
      <Route path={/^\/console(?:\/.*)?$/} component={ConsoleRouter} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  useReferralCapture();
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
